import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { FINANCIAL_SERVICE_ENTRIES, financialSlug, type FinancialServiceEntry } from './FinancialServices';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=800&fit=crop';

// "N/A" is a placeholder for "no address on file" — treat it as absent.
function cleanAddress(e: FinancialServiceEntry) {
  const a = (e.address || '').trim();
  return a && a !== 'N/A' ? a : undefined;
}

export default function FinancialServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('financial-services', FINANCIAL_SERVICE_ENTRIES, (e, i) =>
    slugify(e.name || String(i))
  );

  const entry: FinancialServiceEntry | undefined = mc.items.find(
    (e: any) => financialSlug(e.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Lora', serif" }}
      >
        <SEO
          title="Listing Not Found | Dallas Desi Scoop"
          description="This financial services listing could not be found."
          canonical="/financial-services"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Listing not found.</p>
        <Link
          to="/financial-services"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Financial Services
        </Link>
      </div>
    );
  }

  const displayName = entry.name;
  const address = cleanAddress(entry);
  // Individual detail pages get their own longer SEO description when set
  // (via `detailDescription`), falling back to the short card blurb.
  const longDescription = entry.detailDescription || entry.description;
  const city = address?.split(',').slice(-2, -1)[0]?.trim() || 'Dallas–Fort Worth';
  const title = `${displayName} | Financial Services in ${city} | Dallas Desi Scoop`;
  const description =
    entry.detailDescription ||
    `${displayName}${entry.description ? ` — ${entry.description}` : ' offers financial services'} for the South Asian community in ${city}. Find contact info${address ? ', address' : ''} and more on Dallas Desi Scoop.`;
  const canonicalPath = `/financial-services/${slug}`;

  // Split the stored address into a street portion and a locality (city) so the
  // PostalAddress carries a real addressLocality field, not just one combined string.
  const addressParts = (address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : undefined;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: displayName,
      image: entry.image || FALLBACK_IMAGE,
      description: longDescription || 'Financial services for the South Asian community in Dallas–Fort Worth',
      address: address
        ? {
            '@type': 'PostalAddress',
            streetAddress: streetAddress,
            addressLocality: addressLocality || undefined,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      telephone: entry.phone || undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Financial Services', item: `${BASE_URL}/financial-services` },
        { '@type': 'ListItem', position: 3, name: displayName, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Lora', serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/financial-services"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Financial Services
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium max-w-[220px] sm:max-w-none truncate">
              {displayName}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[24px] sm:text-[34px] font-extrabold leading-tight tracking-tight max-w-[22ch]"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {displayName}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {address && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {address}
              </span>
            )}
            {entry.description && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">{entry.description}</span>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {displayName}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.detailDescription
              ? entry.detailDescription
              : `${displayName} provides ${entry.description ? entry.description.toLowerCase() : 'financial services'} for the South Asian community across Dallas–Fort Worth.${address ? ` Located at ${address}.` : ''} Contact them directly to learn more about the services they offer.`}
          </p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {address ? (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{address}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" />
                <span className="text-gray-400">Serving the Dallas–Fort Worth area</span>
              </div>
            )}
            {entry.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.phone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          <div className="mt-5">
            <SocialLinks item={{ ...(entry as any), instagram: undefined, instagramUrl: undefined, instagram_url: undefined }} variant="bar" />
          </div>
        </div>
      </div>
    </div>
  );
}
